import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from './services/auth.service';
import { ColorService } from './services/color.service';
import { LanguageService } from './services/language.service';
import { ToastComponent } from './components/toast/toast.component';

const INACTIVE_STATUS_POLL_MS = 15000;

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('web');

  private currentUserSub?: Subscription;
  private inactivePollHandle: ReturnType<typeof setInterval> | null = null;

  constructor(
    private colorService: ColorService,
    private languageService: LanguageService,
    private authService: AuthService
  ) {
    // El servicio se inicializa automáticamente y carga los colores
    // El servicio de idiomas se inicializa automáticamente
  }

  ngOnInit(): void {
    this.handleLegacyArrival();
    this.watchInactiveUserStatus();
  }

  ngOnDestroy(): void {
    this.currentUserSub?.unsubscribe();
    this.stopInactivePolling();
  }

  // Detect users redirected from the deprecated Firebase Hosting origin
  // (barbulls-3caaf.web.app) so the migration can be metered via Cloud Run
  // logs, then strip the marker so it doesn't leak into the address bar
  // or downstream navigations.
  private handleLegacyArrival(): void {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('from') !== 'legacy') return;

    console.info('[barbulls-migration] arrived from legacy Firebase Hosting');

    params.delete('from');
    const qs = params.toString();
    const cleanUrl =
      window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    window.history.replaceState(null, '', cleanUrl);
  }

  // El JWT no carga el `status` (solo identidad), así que un usuario inactivo
  // que el admin acaba de activar seguiría viendo la pantalla de pendiente
  // hasta cerrar sesión. Para evitarlo, mientras el usuario sea inactivo
  // refrescamos su info contra `/auth/validate` cada 15s; cuando el backend
  // devuelve `status: 'active'`, el polling se autoapaga.
  private watchInactiveUserStatus(): void {
    this.currentUserSub = this.authService.currentUser$.subscribe((user) => {
      if (user?.status === 'inactive') {
        this.startInactivePolling();
      } else {
        this.stopInactivePolling();
      }
    });
  }

  private startInactivePolling(): void {
    if (this.inactivePollHandle) return;
    this.inactivePollHandle = setInterval(() => {
      this.authService.validateToken().subscribe({ error: () => {} });
    }, INACTIVE_STATUS_POLL_MS);
  }

  private stopInactivePolling(): void {
    if (this.inactivePollHandle) {
      clearInterval(this.inactivePollHandle);
      this.inactivePollHandle = null;
    }
  }
}
